/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: authoring-common
 * (C) Copyright IBM Corp. 2020
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Flyout, Button, Label, FlexLayout, FlexItem, SVGIcon } from '@ba/ba-ui-carbon-toolkit';
import reset32 from '@ba/ba-graphics/dist/icons-js/reset_32';

import StringResources from 'nls/StringResources';
import CustomColorDialog from './CustomColorDialog';
import '../../stylesheets/all.scss';
import KeyCodes from '../lib/@waca/core-client/js/core-client/ui/KeyCodes';

const colorPickerName = 'colorPicker';

/**
 * Description:
 * The client is responsible for supplying at least the required properties. The client is also responsible for
 * supplying the favorite colors from the paletteService if there is one as well. The selected color is returned
 * in the callback and the client must set the selectedColor property with it and presist the selected color.  
 * This is needed for the Color Picker to show which color is current selected from palette and/or favorites.
 * 
 * The color picker has 3 required properties. 
 *   colors - An array of colors to select from.
 * 	 onColorSelected - A callback prototype which has one parameter, which is the selected color that is returns back.
 *   paletteService - the paletteService object which is needed by the Custom Color dialog for selecting favorite/custom colors
 *  
 * */
export default class ColorPicker extends Component {
	static propTypes = {
		/** Colors to be selected */
		colors: PropTypes.array.isRequired,
		/** Function that will get called on click of a color or resetting to defaultColorResetTo.
		 * onColorSelected(color) {}
		 */
		onColorSelected: PropTypes.func.isRequired,
		/** Palette Service needed to create custom color dialog */
		paletteService: PropTypes.object.isRequired,
		/** the color to restore back to. If no color given, it will use the first color in the colors array */
		defaultColorResetTo: PropTypes.string,
		/** Colors label. It has a limited size, width of the control */
		colorsLabel: PropTypes.string,
		/** Tooltip when you hover on the color picker button */
		title: PropTypes.string,
		/** Selected color to show on the button. NOTE: Must be set by client when a color is selected. Would be passed to the onColorSelected() */
		selectedColor: PropTypes.string,
		/** Favorite colors to be selected. The palette service has a method to retrieve this called getFavoriteColors() */
		favoriteColors: PropTypes.array,
		/** Flag to disable the color picker button */
		disabled: PropTypes.bool,
		/** Flyout.propTypes.placement */
		orientation: Flyout.propTypes.placement,
		/** Custom class name(s) */
		className: PropTypes.string,
		/** Custom css styles. Applied to parent div */
		style: PropTypes.object
	};

	static defaultProps = {
		colorFlyoutOpen: false,
		disabled: false,
		className: '',
		customColorDialogOpen: false
	};

	constructor(props, state) {
		super(props, state);
		this.state = {
			...ColorPicker.defaultProps
		};
		this._handleOpen = this._handleOpen.bind(this);
		this.triggerButton = React.createRef();
		this.attachRef = React.createRef();
	}

	render() {
		const { colorFlyoutOpen, customColorDialogOpen } = this.state;
		const { selectedColor,  title, disabled, className, style } = this.props;
		let orientation = this.props.orientation;
		if (typeof orientation === 'undefined') {
			orientation = (document.body.dir === 'rtl' ? 'bottomLeft' : 'bottomRight');
		}
		return (

			<div
				className={`'clsColorPicker' ${className}`}
				style={style}
			>
				{ customColorDialogOpen && this._renderCustomColor() }
				<Button
					aria-controls={`${colorPickerName}_button`}
					data-tid={`${colorPickerName}_button`}
					title={title}
					disabled={disabled}
					tabIndex={0}
					ref={this.triggerButton}
					variant='frameless'
					onClick={this._handleOpen}
					style={ selectedColor && this._getColorStyle(selectedColor) }
					className={'clsColorPicker_button'}
				/>
				{colorFlyoutOpen &&
					<Flyout
						id={`${colorPickerName}_flyout`}
						data-tid={`${colorPickerName}_flyout`}
						aria-hidden={colorFlyoutOpen}
						placement={orientation}
						domNodeToAttachTo={this.attachRef.current}
						hideArrow
						offset={2}
						onClose={this._handleClose.bind(this)}
						className={'clsColorPicker_flyout'}
					>
						{this._displayFlyout()}
					</Flyout>
				}
			</div>
		);
	}

	_displayFlyout () {
		const { colors, colorsLabel, favoriteColors } = this.props;
		return (
			<FlexLayout direction='column'>
				<FlexLayout direction='row'>
					<FlexItem className='clsColorPicker_topLabel'>
						<Label label={colorsLabel} />
					</FlexItem>
					<SVGIcon
						className={'clsColorPicker_restoreDefault'}
						size='small'
						iconId={reset32.id}
						tabIndex='0'
						verticalAlign={'middle'}
						style={ { color: '#0353E9' } }
						// onKeyDown={e => this.handleKeyDown(e, this.onDeleteFavorite.bind(this))}
						onClick={this._restoreDefault.bind(this)}>
					</SVGIcon>
				</FlexLayout>
				<FlexLayout direction='row' className='clsColorPicker_container'>
					{colors.map(this._displayColors.bind(this))}
				</FlexLayout>
				{favoriteColors && favoriteColors.length > 1 &&
				<FlexLayout direction='column'>
					<FlexItem className='clsColorPicker_label'>
						<Label label={StringResources.get('favorite')} />
					</FlexItem>
					<FlexLayout direction='row' className='clsColorPicker_container'>
						{favoriteColors.map(this._displayColors.bind(this))}
					</FlexLayout>
				</FlexLayout>}
				<FlexItem className='clsColorPicker_customColorContainer' tabIndex={0}>
					<Label
						className='clsColorPicker_customColor'
						label={StringResources.get('modeCustom')}
						onClick={this._showCustomColor.bind(this, true)}
					/>
				</FlexItem>
			</FlexLayout>
		);
	}

	_displayColors (color, index) {
		const { selectedColor } = this.props;
		let style = this._getColorStyle(color);

		style.background = style.backgroundColor;

		let clsName;
		if (selectedColor === color) {
			clsName = 'clsColorPicker_selected_indicator';
		} else {
			clsName = `clsColorPicker_cell`;
		}
		return (
			<FlexItem
				className={clsName}
				style={color && style}
				key={index}
				tabIndex={0}
				datat-index={index}
				data-value={color}
				onClick={this._handleSelect.bind(this, color)}
				onKeyDown={(e) => this._handleKey(e, color)}
			/>
		);
	}

	_handleOpen() {
		this.setState({ colorFlyoutOpen: !this.state.colorFlyoutOpen });
	}

	_handleClose() {
		const { customColorDialogOpen } = this.state;
		// hide this if the color dialog isn't open
		if (!customColorDialogOpen) {
			this.setState({
				colorFlyoutOpen: false
			});
		}
	}

	_getColorStyle(color) {
		const style= {};

		style.color = color;
		style.backgroundColor = color;

		return style;
	}

	_handleSelect(color) {
		this.props.onColorSelected(color);
		this.setState({ colorFlyoutOpen: false });
	}

	_showCustomColor(show) {
		this.setState({ customColorDialogOpen: show });
	}

	_renderCustomColor() {
		const { paletteService } = this.props;

		return <CustomColorDialog
			key='colorDialog'
			paletteService={paletteService}
			isDialogOpen={true}
			paletteType={'ConditionalPalette'}
			onChange={this._handleSelect.bind(this)}
			onCloseDialog={this._showCustomColor.bind(this, false)}
		/>;
	}

	_restoreDefault() {
		const { colors, defaultColorResetTo } = this.props;

		let resetColor = defaultColorResetTo;
		// if no reset given, use the first color in the coloor array
		if (!resetColor) {
			resetColor = colors[0];
		}
		this._handleSelect(resetColor);
		this.setState({
			colorFlyoutOpen: true
		});
	}

	_handleKey(e, color) {
		// if Enter or Space key
		if ((e.keyCode === KeyCodes.ENTER) || (e.keyCode === KeyCodes.SPACE)) {
			this._handleSelect(color);
			e.preventDefault();
		}
	}

}



// WEBPACK FOOTER //
// ./src/js/dialog/ColorPicker.js